import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  Paper,
  Text,
  Stack,
  Group,
  Button,
  ActionIcon,
  Alert,
  Center,
  RingProgress,
  ThemeIcon,
  Box,
} from '@mantine/core';
import {
  IconVideo,
  IconPlayerStop,
  IconTrash,
  IconAlertCircle,
  IconUpload,
} from '@tabler/icons-react';
import { Dropzone } from '@mantine/dropzone';
import { VideoPlayer } from '../UI/VideoPlayer';
import '@mantine/dropzone/styles.css';
import { notifications } from '@mantine/notifications';

interface PantryVideoRecorderProps {
  onVideoReady: (video: Blob) => void;
  onVideoCleared: () => void;
  maxDuration?: number; // in seconds, default 120
  maxFileSize?: number; // in bytes, default 100MB
  disabled?: boolean;
}

type RecorderState = 'idle' | 'recording' | 'recorded';

const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
];

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getSupportedVideoMimeType = (): string => {
  const types = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return 'video/webm';
};

export const PantryVideoRecorder: React.FC<PantryVideoRecorderProps> = ({
  onVideoReady,
  onVideoCleared,
  maxDuration = 120,
  maxFileSize = 100 * 1024 * 1024, // 100MB
  disabled = false,
}) => {
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoFileName, setVideoFileName] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const livePreviewRef = useCallback((node: HTMLVideoElement | null) => {
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
    }
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [videoUrl]);

  const startRecording = useCallback(async () => {
    setError(null);

    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError('Video recording is not supported in this browser. Please try Chrome, Firefox, or Safari.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: true,
      });
      streamRef.current = stream;

      const mimeType = getSupportedVideoMimeType();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const baseMime = mimeType.split(';')[0];
        const blob = new Blob(chunksRef.current, { type: baseMime });
        setVideoBlob(blob);
        setVideoFileName(null);

        const url = URL.createObjectURL(blob);
        setVideoUrl(url);

        setState('recorded');
        onVideoReady(blob);

        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      mediaRecorder.start(1000);
      setState('recording');
      setElapsedTime(0);

      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => {
          const next = prev + 1;
          if (next >= maxDuration) {
            mediaRecorderRef.current?.stop();
            if (timerRef.current) clearInterval(timerRef.current);
          }
          return next;
        });
      }, 1000);
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera access was denied. Please allow camera access in your browser settings and try again.');
      } else {
        setError('Could not access camera. Please check your device settings.');
      }
    }
  }, [maxDuration, onVideoReady]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [state]);

  const clearVideo = useCallback(() => {
    if (videoPreviewRef.current) {
      videoPreviewRef.current.pause();
    }
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    setVideoBlob(null);
    setVideoUrl(null);
    setVideoFileName(null);
    setElapsedTime(0);
    setState('idle');
    setError(null);
    onVideoCleared();
  }, [videoUrl, onVideoCleared]);

  const handleFileSelected = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];

    if (file.size > maxFileSize) {
      notifications.show({
        title: 'File too large',
        message: `Maximum video size is ${Math.round(maxFileSize / (1024 * 1024))}MB.`,
        color: 'orange',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
      return;
    }

    setVideoBlob(file);
    setVideoFileName(file.name);
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setState('recorded');
    onVideoReady(file);
  }, [maxFileSize, onVideoReady]);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelected(Array.from(files));
    }
    // Reset input so the same file can be selected again
    event.target.value = '';
  }, [handleFileSelected]);

  const recordingProgress = (elapsedTime / maxDuration) * 100;

  return (
    <Stack gap="md">
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Video error"
          color="red"
          variant="light"
          radius="lg"
        >
          {error}
        </Alert>
      )}

      {/* Idle State */}
      {state === 'idle' && (
        <Stack gap="sm">
          {/* Record button */}
          <Paper
            withBorder
            p="xl"
            radius="lg"
            style={{
              borderStyle: 'dashed',
              borderColor: 'var(--mantine-color-gray-4)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
            onClick={disabled ? undefined : startRecording}
          >
            <Center>
              <Stack align="center" gap="sm">
                <ThemeIcon
                  size={60}
                  radius="xl"
                  variant="light"
                  color="orange"
                >
                  <IconVideo size={30} />
                </ThemeIcon>
                <Text fw={500} size="lg">
                  {isTouchDevice ? 'Tap' : 'Click'} to record video
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Record your pantry, fridge, or kitchen (max {Math.floor(maxDuration / 60)} minutes)
                </Text>
              </Stack>
            </Center>
          </Paper>

          {/* Or upload a file */}
          <Dropzone
            onDrop={handleFileSelected}
            accept={VIDEO_MIME_TYPES}
            maxSize={maxFileSize}
            disabled={disabled}
            multiple={false}
          >
            <Group justify="center" gap="sm" style={{ pointerEvents: 'none' }} py="sm">
              <IconUpload size={20} stroke={1.5} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">
                or drop a video file here (max {Math.round(maxFileSize / (1024 * 1024))}MB)
              </Text>
            </Group>
          </Dropzone>

          {/* Hidden file input for mobile camera capture */}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
        </Stack>
      )}

      {/* Recording State */}
      {state === 'recording' && (
        <Paper withBorder radius="lg" p="xl">
          <Stack align="center" gap="md">
            {/* Live camera preview */}
            <Box
              style={{
                width: '100%',
                maxWidth: 400,
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
                backgroundColor: '#000',
              }}
            >
              <video
                ref={livePreviewRef}
                autoPlay
                muted
                playsInline
                style={{ width: '100%', display: 'block', minHeight: 200 }}
              />
            </Box>
            <RingProgress
              size={80}
              thickness={5}
              roundCaps
              sections={[{ value: recordingProgress, color: 'red' }]}
              label={
                <Center>
                  <Text fw={700} size="md">
                    {formatTime(elapsedTime)}
                  </Text>
                </Center>
              }
            />
            <Group gap="xs">
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'var(--mantine-color-red-6)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              <Text size="sm" c="red" fw={500}>
                Recording...
              </Text>
            </Group>
            <Button
              color="red"
              leftSection={<IconPlayerStop size={16} />}
              onClick={stopRecording}
              size="md"
            >
              Stop Recording
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Recorded / File Selected State */}
      {state === 'recorded' && videoUrl && (
        <Paper withBorder radius="lg" p="lg">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text fw={500}>Video Preview</Text>
              <Group gap="xs">
                {videoBlob && (
                  <Text size="xs" c="dimmed">
                    {videoFileName ? `${videoFileName} · ${formatFileSize(videoBlob.size)}` : formatFileSize(videoBlob.size)}
                  </Text>
                )}
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={clearVideo}
                  title="Remove video"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                borderRadius: 'var(--mantine-radius-md)',
                overflow: 'hidden',
                backgroundColor: '#000',
              }}
            >
              <VideoPlayer
                ref={videoPreviewRef}
                src={videoUrl}
                controls
                style={{ maxWidth: '100%', maxHeight: 240, display: 'block' }}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Stack>
  );
};
